import { Trash2 } from 'lucide-react';

import { errorMessage } from '../../../lib/errorMessage';
import { Drawer } from '../../../components/Drawer/Drawer';
import { useProductLines } from '../../customers/productLinesQueries';
import { useToast } from '../../../components/Toast';
import { ProjectForm } from './ProjectForm';
import type {
  ProjectCompanyOption,
  ProjectInput,
  ProjectRow,
} from './projects.schema';
import { useSoftDeleteProject, useUpsertProject } from './projectsQueries';
import styles from './projects.module.css';

const FORM_ID = 'project-form';

interface ProjectDrawerProps {
  open: boolean;
  project: ProjectRow | null;
  companies: ProjectCompanyOption[];
  defaultCompanyId?: string;
  defaultProductLineId?: string;
  orgId: string | null;
  userId: string | null;
  onClose: () => void;
}

export function ProjectDrawer({
  open,
  project,
  companies,
  defaultCompanyId,
  defaultProductLineId,
  orgId,
  userId,
  onClose,
  onDeleted,
}: ProjectDrawerProps & { onDeleted?: () => void }) {
  const toast = useToast();
  const upsertProject = useUpsertProject(orgId, userId);
  const softDeleteProject = useSoftDeleteProject(orgId);
  const { data: productLines = [] } = useProductLines(orgId);
  const isEditing = Boolean(project);
  const busy = upsertProject.isPending || softDeleteProject.isPending;

  async function handleSubmit(values: ProjectInput) {
    try {
      await upsertProject.mutateAsync({ id: project?.id ?? null, values });
      toast.success('הפרויקט נשמר');
      onClose();
    } catch (err) {
      toast.error(`לא הצלחנו לשמור · ${errorMessage(err)}`);
    }
  }

  async function handleDelete() {
    if (!project) return;
    if (!window.confirm(`למחוק את ${project.name}? המשימות והחשבוניות שלו יימחקו איתו.`)) return;

    try {
      await softDeleteProject.mutateAsync(project.id);
      toast.success('הפרויקט נמחק');
      onClose();
      onDeleted?.();
    } catch (err) {
      toast.error(`לא הצלחנו למחוק · ${errorMessage(err)}`);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="פרויקט"
      title={isEditing ? project?.name : 'פרויקט חדש'}
      ariaLabel={isEditing ? 'עריכת פרויקט' : 'פרויקט חדש'}
      footer={
        <>
          {isEditing ? (
            <button
              type="button"
              className={styles.btnDanger}
              disabled={busy}
              onClick={() => {
                void handleDelete();
              }}
            >
              <Trash2 size={15} aria-hidden="true" />
              מחק
            </button>
          ) : null}
          <button type="button" className={styles.btnSecondary} disabled={busy} onClick={onClose}>
            בטל
          </button>
          <button type="submit" form={FORM_ID} className={styles.btnPrimary} disabled={busy}>
            שמור
          </button>
        </>
      }
    >
      <ProjectForm
        project={project}
        companies={companies}
        {...(defaultCompanyId ? { defaultCompanyId } : {})}
        {...(defaultProductLineId ? { defaultProductLineId } : {})}
        productLines={productLines}
        formId={FORM_ID}
        disabled={busy}
        onSubmit={handleSubmit}
      />
    </Drawer>
  );
}
